import { CMSModal } from "@/context";
import Button from "@/designs/Button";
import Input from "@/designs/Input";
import Modal from "@/designs/Modal";
import Select from "@/designs/Select";
import {
  DeliveryEnum,
  DeliveryType,
  GetDeliveriesDocument,
  useCreateDeliveryMutation,
  useGetOfficeLocationsQuery,
  useGetUsersLazyQuery,
  User,
} from "@/graphql/generated/schema";
import { CircleX } from "lucide-react";
import React, { useContext, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
interface AddDeliveryProp {
  isOpen: boolean;
  onClose: () => void;
  delivery?: DeliveryType;
}
const AddDelivery = ({ isOpen, onClose, delivery }: AddDeliveryProp) => {
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState("");
  const { theme, selectedLocation } = useContext(CMSModal);
  const [locationId, setLocationId] = useState<string | null>(selectedLocation);
  const [deliveredAt, setDeliveredAt] = useState("");
  const [note, setNote] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState<User | null>(null);
  const pickerRef = React.useRef<HTMLInputElement>(null);

  const formatDisplayDate = (iso: string) => {
    if (!iso) return "";

    const date = new Date(iso);
    return date.toLocaleString(
      i18n.language === "zh"
        ? "zh-CN"
        : i18n.language === "fr"
          ? "fr-FR"
          : i18n.language === "it"
            ? "it-IT"
            : "en-US",
      {
        month: "long",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      },
    );
  };

  const { data: locationsData } = useGetOfficeLocationsQuery();
  const locationOptions = (locationsData?.getOfficeLocations ?? []).map(
    (loc) => ({ value: loc?.id ?? "", label: loc?.name ?? "" }),
  );

  const [employee, { data, loading }] = useGetUsersLazyQuery();
  const employees = data?.getUsers?.user || [];
  const handleSearch = (searchTerm: string) => {
    setSearch(searchTerm);
    employee({ variables: { search: searchTerm } });
  };
  useEffect(() => {
    if (isOpen) {
      const now = new Date().toISOString().slice(0, 16);
      setDeliveredAt(now);
      setLocationId(selectedLocation);
    }
  }, [isOpen]);

  const [notifyEmployee, { loading: deliveryLoading }] =
    useCreateDeliveryMutation({
      refetchQueries: [GetDeliveriesDocument],
    });
  const handleSubmit = async () => {
    await notifyEmployee({
      variables: {
        input: {
          reciepient: selectedEmployee?.id,
          deliveryType: DeliveryEnum.Recipient,
          location: locationId as string,
          note: note || undefined,
        },
      },
    });
    toast.success(t("deliveries.addDelivery.successMessage"));
    setSearch("");
    setSelectedEmployee(null);
    setDeliveredAt("");
    setNote("");
    setLocationId(null);
    onClose();
  };
  return (
    <Modal
      onClose={onClose}
      isOpen={isOpen}
      title={t("deliveries.addDelivery.title")}
    >
      <div className="space-y-4">
        <Select
        label={t("deliveries.addDelivery.location")}
        value={locationId ?? ""}
        options={locationOptions}
        onChange={(value) => setLocationId(value)}
        placeholder={t("deliveries.addDelivery.locationPlaceholder")}
      />
      <Input
        label={t("deliveries.addDelivery.recipient")}
        placeholder={t("deliveries.addDelivery.searchPlaceholder")}
        onChange={(value) => handleSearch(value)}
  
        value={search}
        icon={
          selectedEmployee && (
            <CircleX
              className=" cursor-pointer hover:block"
              size={16}
              onClick={() => {
                setSearch("");
                setSelectedEmployee(null);
              }}
            />
          )
        }
      />
      {loading && (
        <div className="text-sm text-gray-400">
          {t("deliveries.addDelivery.searching")}
        </div>
      )}
      {!loading && search && employees.length === 0 && (
        <div className="text-sm text-gray-400">
          {t("deliveries.addDelivery.noResults", { search })}
        </div>
      )}
      {!loading && !selectedEmployee && search && employees.length > 0 && (
        <div
          className={twMerge(
            "border rounded-md max-h-48 overflow-auto",
            theme === "dark"
              ? "border-gray-700 bg-[#1f1f1f]"
              : "border-gray-200 bg-white",
          )}
        >
          {employees.map((emp: any) => (
            <div
              key={emp.id}
              className={twMerge(
                "px-3 py-2 text-sm cursor-pointer transition-colors",
                theme === "dark"
                  ? "hover:bg-gray-700 text-gray-200"
                  : "hover:bg-gray-100 ",
              )}
              onClick={() => {
                setSelectedEmployee(emp);
                setSearch(`${emp.firstName} ${emp.lastName}`);
              }}
            >
              <div className="font-medium">
                {emp.firstName} {emp.lastName}
              </div>
              <div className="text-xs opacity-70">{emp.email}</div>
            </div>
          ))}
        </div>
      )}
      <div >
        <label
          className={twMerge(
            "block text-sm font-medium",
            theme === "dark" ? "text-gray-300" : "text-gray-700",
          )}
        >
          {t("deliveries.addDelivery.dateDelivered")}
        </label>

        {/* Visible formatted input */}
        <input
          type="text"
          readOnly
          value={formatDisplayDate(deliveredAt)}
          onClick={() => pickerRef.current?.showPicker()}
          placeholder="February 3, 2026 4:30 PM"
          className={twMerge(
            "w-full min-w-[260px] lg:min-w-[348px] h-[40px] border rounded-md focus:outline-none focus:ring-1 focus:ring-[#099ed1] transition p-1 cursor-pointer text-gray-500",
            theme === "dark"
              ? "bg-[#262727] border-gray-700 text-gray-100 placeholder-gray-500"
              : "bg-white border-gray-300  placeholder-gray-400",
          )}
        />

        {/* Hidden native picker */}
        <input
          ref={pickerRef}
          type="datetime-local"
          value={deliveredAt}
          onChange={(e) => setDeliveredAt(e.target.value)}
          className="opacity-0 pointer-events-none text-gray-400"
        />
      </div>{" "}
      <div className="space-y-1">
        <label
          className={twMerge(
            "block text-sm font-medium",
            theme === "dark" ? "text-gray-300" : "text-gray-700",
          )}
        >
          {t("deliveries.viewDelivery.note")}
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("deliveries.viewDelivery.notePlaceholder")}
          rows={3}
          className={twMerge(
            "px-3 py-2 border rounded-md w-full transition-colors focus:outline-none focus:ring-1 focus:ring-[#099ed1] resize-none text-sm",
            theme === "dark"
              ? "bg-[#262727] border-gray-700 text-gray-100 placeholder-gray-600"
              : "bg-white border-gray-300",
          )}
        />
      </div>
      <Button
        disabled={!selectedEmployee || !deliveredAt || !locationId}
        loading={deliveryLoading}
        onClick={handleSubmit}
      >
        {t("deliveries.addDelivery.notifyButton")}
      </Button>
      </div>
    </Modal>
  );
};

export default AddDelivery;
